import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import lodash from 'lodash';
import moment from 'moment';

import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTop,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  Notification,
  NotificationBody,
} from '@adeccoux/tag-ds';
import { useInjection } from '@context/inversify-context-provider';
import { Link, LinkSkeleton } from '@features/oneOnboarding/controls/fields/Link';
import { OOStepsEnum } from '@features/oneOnboarding/interfaces';
import { OOFlowWrapper } from '@features/oneOnboarding/wrappers/FlowWrapper';
import ActionHistory from '@features/oneOnboardingAdmin/pages/NewCandidateProfilePage/CandidateInfo/ActionHistory';
import { DeleteCandidateDialog } from '@features/oneOnboardingAdmin/pages/NewCandidateProfilePage/CandidateInfo/DeleteCandidateDialog';
import { useUserInfo } from '@features/oneOnboardingAdmin/pages/NewCandidateProfilePage/CandidateInfo/hooks';
import { getSeverityDetails } from '@helpers/onBoarding.helper';
import {
  mutateCompleteStep,
  mutateDeleteUser,
  mutateUnRejectUser,
  useGetOnboardingsWithRelationsMultiple,
  useGetUser,
  useGetUserProfile,
  useUserCheck,
  useUserOOFlow,
} from '@hooks/apiHooks';
import { Tenant } from '@hooks/useComponentMapping';
import { useDecodedParams } from '@hooks/useDecodedParams';
import { useInterval } from '@hooks/useInterval';
import useTelemetry from '@hooks/useTelemetry';
import { TYPES } from '@libs/InversifyType';
import { IntegrationStateFaq } from '@models/integrationStateFaq.model';
import { OnboardingStatus } from '@models/onboarding.model';
import { EventType, TargetName, TelemetryActor } from '@models/telemetry.model';
import { CheckUser, UserRole } from '@models/user.model';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Dialog, Typography } from '@mui/material';
import { RootState } from '@store/rootReducer';
import { snackbarSlice } from '@store/slices/snackbar';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { frForbiddenDepartments } from '../../../../../constants/frForbiddenDepartments';
import { AamBackendApi } from '../../../../../libs/aamBackendApi';
import { AdminRejectReasonPrompt } from '../../../components/AdminRejectReasonPrompt/AdminRejectReasonPrompt';

import ChangeFlowStepModal from './ChangeFlowStepModal';
import CustomeIntegrationStatusMessage from './CustomeIntegrationStatusMessage';
import InformationList from './InformationList';
import IntegrationErrorsModal from './IntegrationErrorsModal';
import IntegrationStateEditModal from './IntegrationStateEditModal';
import IntegrationStateModal from './IntegrationStateModal';
import OnboardingCustomDataEditModal from './OnboardingCustomDataEditModal';
import { RetriggerIntegrationDialog } from './RetriggerIntegrationDialog';

import styles from './CandidateInfo.module.scss';

interface CandidateInfoProps {
  flowWrapper: OOFlowWrapper;
}

interface CandidateJourney {
  id: string;
  applicationId: string;
  requisitionId: string;
  siteCode: string;
}

const IgnoreDISYSpecialCaseFromIntegrationState = {
  integrationStateKey: 'updateOnboardingCommand',
  value:
    'Could not handle update onboarding command. The LocalEmployeeId was not provided in the Request. ZNumber cannot be Updated.',
  path: 'onboardingResponseData.data.message',
};

const candidateResponseInIntegrationState = [
  {
    integrationCommand: 'updateCandidateCommand',
    values: ['error', false],
    path: 'candidateResponseData.data',
  },
  {
    integrationCommand: 'createCandidateCommand',
    values: ['error', false],
    path: 'candidateResponseData.data',
  },
];

enum PathToIntegrationStateData {
  CandidateResponseData = 'candidateResponseData.data',
}

export const CandidateInfo: FC<CandidateInfoProps> = ({ flowWrapper }) => {
  const [candidateJourneys, setCandidateJourneys] = useState<CandidateJourney[]>([]);
  const [selectedCandidateJourneyId, setSelectedCandidateJourneyId] = useState<string | null>(null);
  const [currentBirthDepartment, setCurrentBirthDepartment] = useState<string | ''>('');
  const [selectedCandidateJourney, setSelectedCandidateJourney] = useState<CandidateJourney | null>(null);
  const [integrationModalVisible, setIntegrationModalVisible] = useState(false);
  const [integrationEditModalVisible, setIntegrationEditModalVisible] = useState(false);
  const [onboardingCustomDataModalVisible, setOnboardingCustomDataModalVisible] = useState(false);
  const [stepChangeModalVisible, setStepChangeModalVisible] = useState(false);
  const [actionHistory, setActionHistory] = useState(false);
  const [integrationErrorsModalVisible, setIntegrationErrorsModalVisible] = useState(false);
  const [hasHiringFeature, setHasHiringFeature] = useState(flowWrapper?.steps?.find((x) => x.name === 'HIRE'));
  const [disableReject, setDisableReject] = useState(false);
  const [rejectPrompt, setRejectPrompt] = useState(false);
  const [deleteUserModal, setDeleteUserModal] = useState<boolean>(false);
  const [resetJourneyUserModal, setResetJourneyUserModal] = useState<boolean>(false);
  const [skipDeletionMail, setSkipDeletionMail] = useState<boolean>(false);
  const [integrationStateFaq, setIntegrationStateFaq] = useState<IntegrationStateFaq[] | null>(null);
  const [tryCount, setTryCount] = useState<number>(0);
  const [localMoUrlWasUpdated, setLocalMoWasUpdated] = useState<boolean>(false);
  const [showRetriggerDialog, setShowRetriggerDialog] = useState<boolean>(false);
  const [integrationStateError, setIntegrationStateError] = useState<boolean>(false);
  const [isHireButtonDisabled, setIsHireButtonDisabled] = useState<boolean>(false);
  const [onboardingErrorFiltered, setOnboardingErrorFiltered] = useState<any>({});
  const [isOpenCustomIntegrationStatusDialoge, setIsOpenCustomIntegrationStatusDialoge] = useState(false);
  const [friendlyErrorMessages, setFriendlyErrorMessages] = useState<any>([]);

  const FETCH_INTERVAL_MS = 2 * 1000;
  const MAX_TRY_COUNT = (120 * 1000) / FETCH_INTERVAL_MS;
  const IS_DISY_NOTIFICATION_SHOWN = 'isDisyNotificationShow';

  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const appSettings = useSelector((state: RootState) => state.featureConfiguration.appSettings);
  const stateUserRole = useSelector((state: RootState) => state.authAdmin.userRole);
  const stateUser = useSelector((state: RootState) => state.authAdmin.user);
  const userRole = stateUser?.role || stateUserRole;
  const { themeAdditionalData } = useSelector((state: RootState) => state.oneOnboarding);
  const storeDispatch = useDispatch();
  const history = useHistory();
  const { tenantId } = useSelector((state: RootState) => state.tenant);
  const { t } = useTranslation(['recruiter', 'control-items', 'integration-state-faq']);
  const queryClient = useQueryClient();
  const { clientOrganizationId, userId, applicationId, configurationId } = useDecodedParams();
  const { data: user, refetch: refetchUser } = useGetUser(tenantId, userId);
  const { data: userFlowWithCurrentStep } = useUserOOFlow(clientOrganizationId, configurationId, userId, applicationId);
  const { data: userProfile, refetch: refetchUserProfile } = useGetUserProfile(tenantId, userId);
  const [showHireNotification, setShowHireNotification] = useState<boolean>(false);
  const { createTelemetryAuthenticated } = useTelemetry();
  const showUnder18warning =
    userFlowWithCurrentStep?.selectedConfiguration?.additionalConfigurations?.showUnder18warning;
  const hideProfileDeleteButtonRecruiter =
    userFlowWithCurrentStep?.selectedConfiguration?.additionalConfigurations?.hideProfileDeleteButtonRecruiter;

  const exportConfig =
    userFlowWithCurrentStep?.selectedConfiguration?.exports?.filter((x: any) => x.type === 'approved') || [];
  const hasPdfExport = exportConfig.length > 0;
  const allowExportApprovedPDF = exportConfig[0]?.allowExportApprovedPDF;

  const { data: onboarding, refetch: refetchOnboarding } = useGetOnboardingsWithRelationsMultiple(
    clientOrganizationId,
    configurationId,
    userId,
    applicationId,
  );

  const filterIntegrationState = useCallback(
    (state: any) => {
      const disySpecialCase = IgnoreDISYSpecialCaseFromIntegrationState;
      const evaluatedValue = lodash.get(state, disySpecialCase.integrationStateKey);
      const hasErrorKey = evaluatedValue === 'error';
      const hasExpectedErrorValue = lodash.get(state, disySpecialCase.path) === disySpecialCase.value;

      if (hasErrorKey && hasExpectedErrorValue) {
        const {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          [disySpecialCase.integrationStateKey]: _,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          [`${disySpecialCase.integrationStateKey}Error`]: __,
          ...rest
        } = state;
        state = rest;
      }

      return state;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onboarding?.integrationState],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getFriendlyMessages = useCallback(
    (data: any) => {
      const messages = [];
      for (const status of candidateResponseInIntegrationState) {
        const isCommandExist = lodash.get(data, status.integrationCommand);
        if (!isCommandExist) continue;

        const isCommandValueMatches = status.values.includes(isCommandExist);
        if (!isCommandValueMatches) continue;

        if (status.path === PathToIntegrationStateData.CandidateResponseData) {
          const responseData = lodash.get(data, status.path) || {};
          if (!Object.keys(responseData)?.length) continue;
          messages.push(...handleUpdateOrCreateCandidateCommandResponseForFrance(responseData));
        }
      }

      return messages;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onboarding?.integrationState],
  );

  const userCheck: CheckUser | undefined = onboarding
    ? {
        clientOrganizationId: onboarding.clientOrganizationId,
        userId: onboarding.userId,
        tenantId: onboarding.tenantId,
        applicationId: onboarding.applicationId || '',
        configurationId: onboarding.configurationId,
        rejectedAt: onboarding.rejectedAt,
      }
    : undefined;

  const { onboardingChecked, refetchOnboardingCheck } = useUserCheck(onboarding);

  useEffect(() => {
    if (onboarding?.integrationState) {
      setOnboardingErrorFiltered(filterIntegrationState(onboarding?.integrationState));
    }
  }, [filterIntegrationState, onboarding?.integrationState]);

  useEffect(() => {
    if (onboarding?.integrationState) {
      setFriendlyErrorMessages((prev: any) => [...prev, ...getFriendlyMessages(onboarding?.integrationState)]);
    }
  }, [onboarding?.integrationState, getFriendlyMessages]);

  useEffect(() => {
    setHasHiringFeature(flowWrapper?.steps?.find((x) => x.name === 'HIRE'));
    setDisableReject(
      onboarding?.status === OnboardingStatus.hired ||
        onboarding?.status === OnboardingStatus.approved ||
        (hasHiringFeature !== undefined &&
          flowWrapper?.steps?.findIndex((s) => s.name === userFlowWithCurrentStep?.currentStep) >=
            flowWrapper?.steps?.findIndex((s) => s.name === OOStepsEnum.recruiterSignDocuments)),
    );
    const bDepartment = flowWrapper.getUserValueOrDefault(['REGION2_OF_BIRTH_2', 'REGION2_OF_BIRTH'], 'string');
    setCurrentBirthDepartment(bDepartment);
    if (userCheck) {
      refetchOnboardingCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowWrapper, onboarding]);

  const { mutate: deleteUser, isPending: deleting } = mutateDeleteUser(tenantId, userId, () => {
    refetchUserProfile();
    refetchOnboarding();
    refetchUser();
    setDeleteUserModal(false);
    setSkipDeletionMail(false);
    history.push(`/oo/recruiter/`);
  });

  const { mutate: unRejectUser } = mutateUnRejectUser(
    clientOrganizationId,
    configurationId,
    userId,
    applicationId,
    () => {
      refetchUserProfile();
      refetchOnboarding();
      refetchUser();
    },
  );

  const updateUserProfilePdfExported = async () => {
    if (!userProfile?.customData?.pdfExported) {
      userProfile!.customData!.pdfExported = true;
      await aamBackendApi.updateUserProfile(tenantId, userId, userProfile);
      refetchUserProfile();
    }
  };

  const exportApprovedPDF = async (
    clientOrganizationId: string,
    configurationId: string,
    userId: string,
    applicationId: string,
  ) => {
    const resp = await aamBackendApi.exportApprovedPDF(clientOrganizationId, configurationId, userId, applicationId);
    const fileName = resp.headers['content-disposition']?.split('filename=')[1] || 'candidatesList.pdf';
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    await updateUserProfilePdfExported();
  };

  const exportAllDocumentsPDF = async (clientOrganizationId: string, configurationId: string, userId: string) => {
    const resp = await aamBackendApi.exportAllDocumentsPDF(userId, clientOrganizationId, configurationId);
    const fileName = resp.headers['content-disposition']?.split('filename=')[1] || 'candidatesList.pdf';
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    await updateUserProfilePdfExported();
  };
  const { mutateAsync: approveCandidate } = mutateCompleteStep(
    clientOrganizationId,
    configurationId,
    userId,
    applicationId,
    OOStepsEnum.approve,
    queryClient,
    async () => {
      if (hasPdfExport) {
        await exportAllDocumentsPDF(clientOrganizationId, configurationId, userId);
        if (allowExportApprovedPDF) {
          await exportApprovedPDF(clientOrganizationId, configurationId, userId, applicationId);
        }
      }
      refetchOnboardingCheck();

      if (themeAdditionalData?.showNotificationOnApprove) {
        storeDispatch(
          snackbarSlice.actions.showSuccess({
            message: t('recruiter:GENERAL.CANDIDATES_OVERVIEW.candidateApproved'),
          }),
        );
      }
    },
  );

  const { mutate: hireCandidate } = mutateCompleteStep(
    clientOrganizationId,
    configurationId,
    userId,
    applicationId,
    OOStepsEnum.hire,
    queryClient,
    () => {
      refetchOnboardingCheck();
    },
  );

  const { phoneNumber, address, dateOfBirth, client, clientDisplayName, journey } = useUserInfo(
    tenantId,
    flowWrapper,
    userProfile,
  );

  const isDisyButtonVisible = onboardingChecked?.hasHiringFeature && onboarding?.localMoUrl;
  const rejectFeatureEnabled = !(
    appSettings?.allowedBulkActions?.length && !appSettings.allowedBulkActions.find((item) => item.name === 'REJECT')
  );
  const visibleButton = {
    localSystem: isDisyButtonVisible,
    unReject: (onboarding?.status === 'rejected' || onboarding?.rejectedAt !== null) && user?.deletedAt === null,
    reject: rejectFeatureEnabled && !(onboarding?.status === 'rejected' || onboarding?.rejectedAt !== null),
  };

  const candidateIsUnder18 = useMemo(() => {
    const eighteenYearsLater = moment(dateOfBirth).add(18, 'years');
    return moment().isBefore(eighteenYearsLater) && showUnder18warning;
  }, [dateOfBirth, showUnder18warning]);

  const pollForDisyLink = useMemo(() => {
    const shouldPoll =
      onboardingChecked?.hasHiringFeature &&
      !onboarding?.localMoUrl &&
      tryCount <= MAX_TRY_COUNT &&
      (userFlowWithCurrentStep?.currentStep as string) === OOStepsEnum.awaitContract;

    if (shouldPoll) {
      localStorage.setItem(IS_DISY_NOTIFICATION_SHOWN, 'false');
    }

    return shouldPoll;
  }, [
    MAX_TRY_COUNT,
    onboarding?.localMoUrl,
    onboardingChecked?.hasHiringFeature,
    tryCount,
    userFlowWithCurrentStep?.currentStep,
  ]);

  useMemo(() => {
    if (isDisyButtonVisible && localStorage.getItem(IS_DISY_NOTIFICATION_SHOWN) === 'false') {
      setShowHireNotification(true);

      localStorage.removeItem(IS_DISY_NOTIFICATION_SHOWN);

      setTimeout(() => {
        setShowHireNotification(false);
      }, 3000);
    }
  }, [isDisyButtonVisible]);

  useEffect(() => {
    const userIntegrationState = onboardingErrorFiltered ?? {};
    const hasIntegrationStateAnyError = Object.values(userIntegrationState).some((val) => val === 'error');
    setIntegrationStateError(hasIntegrationStateAnyError);
  }, [onboarding?.updatedAt, onboarding?.integrationState, onboardingErrorFiltered]);

  useEffect(() => {
    setCandidateJourneys(onboarding?.customData?.journeys ?? []);
    setSelectedCandidateJourneyId(onboarding?.customData?.selectedCandidateJourneyId);
  }, [onboarding]);

  useEffect(() => {
    setSelectedCandidateJourney(candidateJourneys.find((j) => j.id === selectedCandidateJourneyId) || null);
  }, [selectedCandidateJourneyId, candidateJourneys]);

  const onCandidateJourneySelect = (selectedId: string) => {
    if (selectedId) {
      setSelectedCandidateJourneyId(selectedId);

      if (!onboarding?.applicationId) {
        return;
      }

      aamBackendApi.setCandidateJourney(
        onboarding.tenantId,
        onboarding.clientOrganizationId,
        onboarding.configurationId,
        onboarding.applicationId,
        onboarding.userId,
        selectedId,
      );
    }
  };

  const { data: integrationStateFaqData, refetch: getIntegrationStateFaq } = useQuery<IntegrationStateFaq[]>({
    queryKey: ['getIntegrationStateFaq', tenantId],
    queryFn: () => aamBackendApi.getIntegrationStateFaq(tenantId),
    refetchOnMount: true,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (integrationStateFaqData) {
      const integrationState = JSON.stringify(onboarding?.integrationState);
      const explanations: IntegrationStateFaq[] = [];

      integrationStateFaqData.forEach((x: IntegrationStateFaq) => {
        const replaceSpecialSymbols = (word: string) => {
          if (!word) return '';

          return word.replace(/\[|\]|\\|\//g, '');
        };

        const phraseWithoutChars = replaceSpecialSymbols(x.phrase);
        if (new RegExp(`\\b${phraseWithoutChars}\\b`).test(replaceSpecialSymbols(integrationState))) {
          explanations.push(x);
        }
      });

      setIntegrationStateFaq(explanations);
    }
  }, [integrationStateFaqData, onboarding?.integrationState]);

  const resetCandidateJourney = async () => {
    if (onboarding?.applicationId) {
      await aamBackendApi.resetCandidateJourney(
        onboarding.clientOrganizationId,
        onboarding.configurationId,
        onboarding.userId,
        onboarding.applicationId,
      );
      window.location.reload();
    }
    setResetJourneyUserModal(false);
  };

  useInterval(() => {
    if (!pollForDisyLink) return;

    const asyncCall = async () => {
      const { data: onb } = await refetchOnboarding();
      setTryCount((prev) => prev + 1);
      if (onb?.localMoUrl) {
        setLocalMoWasUpdated(true);
      }
    };
    asyncCall();
  }, FETCH_INTERVAL_MS);

  useEffect(() => {
    if (localMoUrlWasUpdated) {
      window.open(onboarding?.localMoUrl, '_blank');
      return;
    }

    if (tryCount > MAX_TRY_COUNT) {
      setShowRetriggerDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localMoUrlWasUpdated, tryCount]);

  useEffect(() => {
    if (onboarding) {
      getIntegrationStateFaq();
    }
  }, [onboarding, getIntegrationStateFaq]);

  const renderHealthSeverityRating = (score: number, isHighScoreAnswers: boolean) => {
    if (isNaN(score)) return;

    const { label, image, alt } = getSeverityDetails(score, isHighScoreAnswers);
    return (
      <div className={styles.healthSeverityRating}>
        {label} &nbsp;
        <img src={image} alt={alt} />
      </div>
    );
  };
  const syncEmailWithAamBackend = useCallback(async () => {
    if (
      window.confirm(
        'Are you sure you want to sync the email with AAM backend, this may change the email in global onboarding?',
      )
    ) {
      try {
        const res = await aamBackendApi.syncEmailWithAamBackend(tenantId, userId);
        if (!res) return;
        if (res?.updated) {
          storeDispatch(
            snackbarSlice.actions.showSuccess({
              message: `Email synced successfully with AAM backend, new email: ${res.email}`,
            }),
          );
          refetchUser();
        }
        if (!res?.updated) {
          storeDispatch(
            snackbarSlice.actions.showSuccess({
              message: `Email is already up to date with AAM backend: ${res.email}`,
            }),
          );
        }
      } catch (err) {
        storeDispatch(
          snackbarSlice.actions.showError({
            message: err,
          }),
        );
      }
    }
  }, [aamBackendApi, tenantId, userId, storeDispatch, refetchUser]);

  return (
    <>
      <div className={styles.candidateInfo}>
        {user && onboarding && userProfile && (
          <>
            <Card className="tag-ds">
              <CardTop className={styles.header}>
                <div className={`status-tag_${onboarding.status === 'rejected' ? 'error' : 'success'}`}>
                  {t(`control-items:GENERAL.STATUS_FILTERS.${onboarding.status?.toUpperCase()}.label`)}
                </div>
                <Dropdown className={styles.menu}>
                  <DropdownButton>
                    <button className="button-card-icon">
                      <span className="material-icons">more_vert</span>
                    </button>
                  </DropdownButton>
                  <DropdownMenu>
                    <div className="dropdown-item-icon" onClick={() => setIntegrationModalVisible(true)}>
                      <span className="material-icons">merge_type</span>
                      {t('recruiter:GENERAL.CANDIDATE_DETAILS.integrationState')}
                    </div>{' '}
                    {userRole === UserRole.adeccoConfig && (
                      <>
                        <div className="dropdown-item-icon" onClick={() => setIntegrationEditModalVisible(true)}>
                          <span className="material-icons">code</span>
                          {t('recruiter:GENERAL.CANDIDATE_DETAILS.integrationStateEdit')}
                        </div>

                        <div className="dropdown-item-icon" onClick={() => setOnboardingCustomDataModalVisible(true)}>
                          <span className="material-icons">edit</span>
                          {t('recruiter:GENERAL.CANDIDATE_DETAILS.onboardingCustomDataEdit')}
                        </div>

                        <div className="dropdown-item-icon" onClick={() => setStepChangeModalVisible(true)}>
                          <span className="material-icons">edit</span>
                          {t('recruiter:GENERAL.CANDIDATE_DETAILS.changeCandidateFlowStep')}
                        </div>

                        <div
                          className="dropdown-item-icon"
                          onClick={() =>
                            history.push(
                              `/oo/recruiter/integration-messages-history/${
                                tenantId && tenantId === Tenant.Australia ? applicationId : user?.aamBeCandidateId
                              }`,
                            )
                          }
                        >
                          <span className="material-icons">forum</span>
                          {t('recruiter:GENERAL.INTEGRATIONMESSAGEHISTORY.title')}
                        </div>
                      </>
                    )}
                    {hideProfileDeleteButtonRecruiter !== true && (
                      <div className="dropdown-item-icon" onClick={() => setDeleteUserModal(true)}>
                        <span className="material-icons">delete</span>
                        {t('recruiter:GENERAL.CANDIDATE_DELETION.deleteCandidate')}
                      </div>
                    )}
                    <div className="dropdown-item-icon" onClick={() => setActionHistory(true)}>
                      <span className="material-icons">pending_actions</span>
                      {t('recruiter:GENERAL.CANDIDATE_DETAILS.actionHistory')}
                    </div>{' '}
                    {onboardingChecked?.resetEnabled && (
                      <div
                        className={`dropdown-item-icon ${styles.witSeparator}`}
                        onClick={() => setResetJourneyUserModal(true)}
                      >
                        <span className="material-icons">repeat</span>
                        {t('recruiter:GENERAL.CANDIDATE_DELETION.resetJourney')}
                      </div>
                    )}
                  </DropdownMenu>
                </Dropdown>
                <div>
                  {frForbiddenDepartments.includes(currentBirthDepartment) && (
                    <Typography className={styles.warningMessage}>
                      {t('recruiter:GENERAL.CANDIDATE_DETAILS.birthDepartmentStateWarning')}
                    </Typography>
                  )}
                </div>
              </CardTop>
              <CardHeader>
                {candidateIsUnder18 && (
                  <h5 className={styles.warningMessage}>
                    {t('recruiter:GENERAL.CANDIDATE_DETAILS.candidateIsUnder18')}
                  </h5>
                )}
                {(userFlowWithCurrentStep?.recruiterAlerts || []).map((recruiterAlert: string) => (
                  <h5 key={recruiterAlert} className={styles.warningMessage}>
                    {t(recruiterAlert)}
                  </h5>
                ))}
                <h4>
                  {flowWrapper.getUserValueOrDefault('FIRST_NAME', 'string')}{' '}
                  {flowWrapper.getUserValueOrDefault('LAST_NAME', 'string')}
                </h4>
                {onboarding?.customData?.healthSeverityScore >= 0 && (
                  <div className={styles.healthSeverity}>
                    Health severity rating
                    {renderHealthSeverityRating(
                      onboarding?.customData?.healthSeverityScore,
                      onboarding?.customData?.highScoreAnswers,
                    )}
                  </div>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    gap: '10px',
                  }}
                >
                  {integrationStateError && (
                    <h5 style={{ color: 'red', fontWeight: 'bold' }}>
                      {t(`recruiter:GENERAL.CANDIDATE_DETAILS.integrationStateError`)}
                    </h5>
                  )}
                  {friendlyErrorMessages.length > 0 && (
                    <InfoOutlinedIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setIsOpenCustomIntegrationStatusDialoge((prev) => !prev)}
                    />
                  )}
                </Box>
                {isOpenCustomIntegrationStatusDialoge && (
                  <CustomeIntegrationStatusMessage
                    friendlyErrorMessages={friendlyErrorMessages}
                    setIsOpenCustomIntegrationStatusDialoge={setIsOpenCustomIntegrationStatusDialoge}
                  />
                )}
                {integrationStateError &&
                  integrationStateFaq?.map((item: IntegrationStateFaq, index) => (
                    <h6 key={index} style={{ color: 'red' }}>
                      {t(`integration-state-faq:${item.explanation}`)}
                    </h6>
                  ))}
              </CardHeader>
              <CardBody>
                <InformationList
                  userData={{
                    email: user.email,
                    phoneNumber: phoneNumber,
                    address: address,
                    client: client,
                    clientDisplayName: clientDisplayName,
                    onboardingApplicationId: onboarding.applicationId,
                    journey: journey,
                    selectedCandidateJourney: selectedCandidateJourney,
                    candidateJourneys: candidateJourneys,
                    onboardingStatus: onboarding.status,
                    selectedCandidateJourneyId: selectedCandidateJourneyId,
                  }}
                  onCandidateJourneySelect={onCandidateJourneySelect}
                  onSyncEmailWithAamBackend={syncEmailWithAamBackend}
                />
              </CardBody>
              <CardFooter>
                {onboardingChecked?.amazonWarning && (
                  <h5 className={styles.warningMessage}>{t('recruiter:GENERAL.CANDIDATE_DETAILS.amazonWarning')}</h5>
                )}
                {visibleButton.unReject && (
                  <button onClick={() => unRejectUser()}>
                    {t(`recruiter:GENERAL.CANDIDATE_DELETION.unRejectCandidate`)}
                  </button>
                )}
                {visibleButton.reject && (
                  <button disabled={disableReject} className="button-secondary" onClick={() => setRejectPrompt(true)}>
                    {t(`recruiter:GENERAL.ONBOARDING_REJECTION.rejectCandidate`)}
                  </button>
                )}
                {hasHiringFeature ? (
                  <>
                    <button
                      disabled={!onboardingChecked?.hiringEnabled || isHireButtonDisabled}
                      onClick={() => {
                        setIsHireButtonDisabled(true);
                        hireCandidate();
                        createTelemetryAuthenticated({
                          applicationId: applicationId,
                          clientOrganizationId: clientOrganizationId,
                          configurationId: configurationId,
                          eventType: EventType.BUTTON_CLICKED,
                          tenantId: tenantId,
                          targetName: TargetName.RecruiterHire,
                          candidateId: userId,
                          actor: TelemetryActor.RECRUITER,
                        });
                      }}
                    >
                      {t(`recruiter:HIRING.HIRE.hireCandidate`)}
                    </button>
                  </>
                ) : (
                  <>
                    <button disabled={!onboardingChecked?.approveEnabled} onClick={() => approveCandidate()}>
                      {t(`recruiter:APPROVAL.APPROVE.approveCandidate`)}{' '}
                    </button>
                  </>
                )}
                {!!userProfile?.customData?.pdfExported && (
                  <button
                    className="button-secondary"
                    onClick={() => {
                      exportAllDocumentsPDF(clientOrganizationId, configurationId, userId);
                      if (allowExportApprovedPDF)
                        exportApprovedPDF(clientOrganizationId, configurationId, userId, applicationId);
                    }}
                  >
                    {t(`recruiter:GENERAL.DOWNLOAD.downloadPDF`)}
                  </button>
                )}
              </CardFooter>
            </Card>
            {visibleButton.localSystem && (
              <Link
                text={t(`recruiter:HIRING.HIRE.go_to_local_system`)}
                onClick={() => window.open(onboarding?.localMoUrl)}
              />
            )}
            {pollForDisyLink && <LinkSkeleton pleaseWaitText={t(`recruiter:HIRING.HIRE.fetching-data-please-wait`)} />}
          </>
        )}
        {integrationModalVisible && (
          <IntegrationStateModal
            integrationState={onboardingErrorFiltered}
            isActive={integrationModalVisible}
            closeModal={() => setIntegrationModalVisible(false)}
          />
        )}
        {integrationEditModalVisible && userRole === UserRole.adeccoConfig && (
          <IntegrationStateEditModal
            integrationState={onboardingErrorFiltered}
            isActive={integrationEditModalVisible}
            closeModal={() => setIntegrationEditModalVisible(false)}
          />
        )}
        {onboardingCustomDataModalVisible && userRole === UserRole.adeccoConfig && (
          <OnboardingCustomDataEditModal
            customData={onboarding?.customData}
            isActive={onboardingCustomDataModalVisible}
            closeModal={() => setOnboardingCustomDataModalVisible(false)}
          />
        )}
        {stepChangeModalVisible && userRole === UserRole.adeccoConfig && (
          <ChangeFlowStepModal
            isActive={stepChangeModalVisible}
            closeModal={() => setStepChangeModalVisible(false)}
            onboardingStatus={onboarding?.status}
          />
        )}
        {integrationErrorsModalVisible && (
          <IntegrationErrorsModal
            integrationState={onboardingErrorFiltered}
            isActive={integrationErrorsModalVisible}
            closeModal={() => setIntegrationErrorsModalVisible(false)}
          />
        )}
        {actionHistory && (
          <ActionHistory userId={userId} isActive={actionHistory} onClose={() => setActionHistory(false)} />
        )}
        <AdminRejectReasonPrompt
          clientOrganizationId={clientOrganizationId}
          configurationId={configurationId}
          userId={userId}
          applicationId={applicationId}
          onClose={() => setRejectPrompt(false)}
          onSuccess={() => {
            refetchUserProfile();
            refetchOnboarding();
            refetchUser();
            history.push('/oo/recruiter');
          }}
          isVisible={rejectPrompt}
        />
        <DeleteCandidateDialog
          onChangeSkipDeletionMail={() => setSkipDeletionMail(!skipDeletionMail)}
          skipDeletionMail={skipDeletionMail}
          open={deleteUserModal}
          onClose={() => setDeleteUserModal(false)}
          onDelete={() => {
            deleteUser({ sendDeletionMail: !skipDeletionMail });
          }}
          deleting={deleting}
        />
        <Dialog
          aria-labelledby="customized-dialog-title"
          open={resetJourneyUserModal}
          onClose={() => setResetJourneyUserModal(false)}
          style={{ overflowY: 'visible' }}
          PaperProps={{
            style: {
              borderRadius: 8,
              width: '667px',
              height: '260px',
            },
          }}
        >
          <div className="tag-ds modal docs">
            <button className="modal-close-button" onClick={() => setResetJourneyUserModal(false)}>
              <span className="material-icons">close</span>
            </button>

            <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
              <h4 className="modal-header">{t('recruiter:GENERAL.CANDIDATE_DELETION.resetJourneyUserMessage')}</h4>
              {/* <div className="modal-body">here we should add message</div> */}
            </Box>
            <div style={{ paddingTop: '12px' }}>
              <div className="modal-footer">
                <button className="button-secondary" onClick={() => setResetJourneyUserModal(false)}>
                  {t(`GENERAL.CANDIDATE_DELETION.dontReset`).toUpperCase()}
                </button>
                <button className="button-primary" onClick={() => resetCandidateJourney()}>
                  {t(`GENERAL.CANDIDATE_DELETION.reset`).toUpperCase()}
                </button>
              </div>
            </div>
          </div>
        </Dialog>
        <RetriggerIntegrationDialog
          open={showRetriggerDialog}
          onClose={() => setShowRetriggerDialog(false)}
          onRetrigger={() => {
            setTryCount(0);
            setShowRetriggerDialog(false);
          }}
        />
      </div>
      <Notification
        icon={<span className="material-icons">info_outline</span>}
        success
        className={`tag-ds ${styles.notification}`}
        show={showHireNotification}
        onClose={() => setShowHireNotification(false)}
      >
        <NotificationBody>
          {t(`recruiter:APPROVAL.APPROVE.approvedForEmployment`, {
            name: userProfile?.firstName + ' ' + userProfile?.lastName,
          })}
        </NotificationBody>
      </Notification>
    </>
  );
};

export default CustomeIntegrationStatusMessage;

function handleUpdateOrCreateCandidateCommandResponseForFrance(data: any) {
  const messages: string[] = [];
  const commandsAndStrings = [
    {
      integrationObjectKey: 'inFOGOELANDCandidateApiResponse',
      errorMessageSubstring: ['Liste des doublons avérés'],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateApiResponseText',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateCallListMemberApiResponse',
      errorMessageSubstring: ['Call list not found for the branch'],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateCallListMemberApiResponseText',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateCallListMemberApiResponse',
      errorMessageSubstring: ['Please select only active contacts'],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateCallListMemberApiResponseText_2',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateCommonDocumentApiResponse',
      errorMessageSubstring: ['The entered BIC is not correct'],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateCommonDocumentApiResponseText',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateApiResponse',
      errorMessageSubstring: ['Un candidat existe déjà avec le même n° sécurité sociale'],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateApiResponse_Erreur_SSN_2',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateApiResponse',
      errorMessageSubstring: ['Liste des doublons avérés'],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateApiResponse_Erreur_SSN_3',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateApiResponse',
      errorMessageSubstring: [
        'Invalid data, please review the information entered in the Birthdate and the Social security number fields',
      ],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateApiResponse_Erreur_SSN_4',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateApiResponse',
      errorMessageSubstring: [
        'Invalid data, please review the information entered for the Department of birth and the Social security number',
      ],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateApiResponse_Erreur_SSN_5',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateApiResponse',
      errorMessageSubstring: ['Full SSN cannot be updated for the candidate in one of the following statuses'],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateApiResponse_Erreur_SSN_6',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateCallListMemberApiResponse',
      errorMessageSubstring: ['Please select only active contacts'],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateCallListMemberApiResponse_Erreur_Fiche_Inactive',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateCommonDocumentApiResponse',
      errorMessageSubstring: ['The entered IBAN is not correct'],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateCommonDocumentApiResponse_Erreur_IBAN_2',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateCommonDocumentApiResponse',
      errorMessageSubstring: ["The BIC entered is not correct, country doesn't match with IBAN country"],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateCommonDocumentApiResponse_Erreur_IBAN_3',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateCommonDocumentApiResponse',
      errorMessageSubstring: ["One or more records have an active IBAN identical to this candidate's IBAN"],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateCommonDocumentApiResponse_Erreur_IBAN_4',
    },
    {
      integrationObjectKey: 'cronJobIntegrationError',
      errorMessageSubstring: ['updateOnboardingCommand with status sent without response for a long time'],
      translationKey: 'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.cronJobIntegrationError',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateApiResponse',
      errorMessageSubstring: [
        'Insert failed. First exception on row 0; first error: INVALID_OR_NULL_FOR_RESTRICTED_PICKLIST, bad value for restricted picklist field',
        'PlaceOfBirthDepartment__c',
      ],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateApiResponse_Error_1',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateApiResponse',
      errorMessageSubstring: ['Multiple Candidates found'],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateApiResponse_Error_MultipleCandidates',
    },
    {
      integrationObjectKey: 'inFOGOELANDCandidateApiResponse',
      errorMessageSubstring: [
        'Impossible to update candidate with "Internal" status. Manual action by recruter is needed',
      ],
      translationKey:
        'recruiter:GENERAL.CANDIDATE_DETAILS.customIntegrationErrorMessages.inFOGOELANDCandidateApiResponse_Error_InternalStatus',
    },
  ];

  for (const commandReponseMessage in data) {
    if (!data[commandReponseMessage]) continue;

    const obj = data[commandReponseMessage][0];

    if (obj?.success === 'true' || !obj?.message) continue;

    commandsAndStrings.forEach((commandAndString) => {
      if (commandReponseMessage === commandAndString.integrationObjectKey) {
        const messageMatched = commandAndString.errorMessageSubstring.every((targetString) => {
          if (obj.message.includes(targetString)) return true;
          else return false;
        });
        if (messageMatched) {
          messages.push(commandAndString.translationKey);
        }
      }
    });
  }

  return messages;
}
